import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "../supabaseClient";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const VIDEOS = [
  {
    id: "meditation-1",
    youtubeId: "6FKEsh26G68",
    titleKey: "meditation.video1.title",
    subtitleKey: "meditation.video1.subtitle",
  },
  {
    id: "meditation-2",
    youtubeId: "rMzg7eBjna0",
    titleKey: "meditation.video2.title",
    subtitleKey: "meditation.video2.subtitle",
  },
];

function normalizeTier(raw: string | null | undefined): string | null {
  if (!raw) return null;
  return raw.toLowerCase().replace(/\s+/g, "_");
}

function isBeginnerTier(tier: string | null): boolean {
  if (!tier) return false;
  return ["beginner", "beginner_monthly", "beginner_yearly"].includes(tier);
}

export default function MeditationLibrary() {
  const { t } = useTranslation();
  const PROFILE_UPDATED_EVENT = "innergem:profile-updated";
  const [subscriptionTier, setSubscriptionTier] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  const [hasLifetimeDeal, setHasLifetimeDeal] = useState(false);
  const [activeVideo, setActiveVideo] = useState<typeof VIDEOS[number] | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData?.user?.id;
      if (!userId) return;

      const { data, error } = await supabase
        .from("user_profiles")
        .select("subscription_tier, role, has_lifetime_deal")
        .eq("id", userId)
        .maybeSingle();

      if (error || !data) return;

      const tier = normalizeTier((data as any).subscription_tier as string | null);
      setSubscriptionTier(tier);
      setRole((data as any).role ?? null);
      setHasLifetimeDeal(!!(data as any).has_lifetime_deal || tier === "lifetime_deal");
    };

    const onProfileUpdated = () => {
      void loadProfile();
    };

    loadProfile();
    window.addEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    return () => {
      window.removeEventListener(PROFILE_UPDATED_EVENT, onProfileUpdated);
    };
  }, []);

  const beginnerLocked = isBeginnerTier(subscriptionTier ?? null) && !hasLifetimeDeal && role !== "admin" && role !== "mentor";

  return (
    <div className="container mx-auto px-4 py-8 pb-24">
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-primary">
          {t("meditation.title", { defaultValue: "Meditation Library" })}
        </h1>
        <p className="text-muted-foreground mt-2 text-sm md:text-base">
          {t("meditation.subtitle", {
            defaultValue:
              "A gentle corner for guided meditations and sound journeys.",
          })}
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {VIDEOS.map((video, index) => {
          const locked = index === 1 && beginnerLocked;
          const title = t(video.titleKey as any);
          const subtitle = t(video.subtitleKey as any);

          const safeTitle =
            title && title !== video.titleKey
              ? title
              : index === 0
              ? "Soft Grounding Meditation"
              : "Deeper Journey Meditation";

          const safeSubtitle =
            subtitle && subtitle !== video.subtitleKey
              ? subtitle
              : index === 0
              ? "A short grounding practice to return to your body."
              : "A deeper journey for when you have more space.";

          return (
            <div
              key={video.id}
              className={`relative rounded-xl border bg-card shadow p-4 md:p-5 flex flex-col justify-between overflow-hidden transition-transform duration-300 hover:scale-[1.02] ${
                locked ? "cursor-not-allowed opacity-80" : "cursor-pointer"
              }`}
              onClick={() => {
                if (locked) return;
                setActiveVideo(video);
              }}
            >
              {/* Placeholder visual */}
              <div className="relative mb-4 overflow-hidden rounded-lg aspect-video bg-gradient-to-br from-purple-200 via-indigo-200 to-pink-200 flex items-center justify-center">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(255,255,255,0.8),_transparent_55%)]" />
                <div className="relative z-10 flex flex-col items-center text-center">
                  <span className="text-xs uppercase tracking-[0.2em] text-purple-700/80 mb-1">
                    {t("meditation.badge", { defaultValue: "Guided Meditation" })}
                  </span>
                  <span className="text-lg md:text-xl font-semibold text-slate-900">
                    {safeTitle}
                  </span>
                </div>
              </div>

              <div className="flex-1 flex flex-col">
                <h2 className="text-base md:text-lg font-semibold text-primary mb-1">
                  {safeTitle}
                </h2>
                <p className="text-sm text-muted-foreground mb-4 flex-1">
                  {safeSubtitle}
                </p>

                {/* Bottom membership hint removed per latest design */}

                {locked && (
                  <div className="absolute inset-0 z-10 flex flex-col items-center justify-center bg-white/40 backdrop-blur-sm">
                    <p className="text-sm font-semibold text-purple-900">
                      {t("meditation.locked.title", {
                        defaultValue: "Available for Intermediate and above",
                      })}
                    </p>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog
        open={!!activeVideo}
        onOpenChange={(open) => {
          if (!open) setActiveVideo(null);
        }}
      >
        {activeVideo && (
          <DialogContent className="max-w-3xl w-[92vw]">
            <DialogHeader>
              <DialogTitle>{t(activeVideo.titleKey as any)}</DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <div className="relative w-full overflow-hidden rounded-lg aspect-video bg-black">
                <iframe
                  className="absolute inset-0 h-full w-full"
                  src={`https://www.youtube.com/embed/${activeVideo.youtubeId}?autoplay=1`}
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
